import { YN } from './Constants';
import {
  EarlyScrapConditionResult,
  EarlyScrapConfirmationMethod,
  EarlyScrapExteriorResult,
  EarlyScrapGlassResult,
  EarlyScrapIdentityResult,
  EarlyScrapLightingResult,
  EarlyScrapOperationalJudgment,
  EarlyScrapOperationResult,
  EarlyScrapPhotoPosition,
  EarlyScrapPowerTransmissionResult,
  EarlyScrapTireResult,
  EarlyScrapVehicleClass,
} from '../enums/EarlyScrapEnums';

/** [별지 제3호 서식] 조기폐차 대상차량 확인(차량상태확인)서 메타 */
const EARLY_SCRAP_FORM_META = {
  formType: '별지 제3호 서식',
  title: '조기폐차 대상차량 확인(차량상태확인)서',
  issuer: '사단법인 한국자동차기술인협회',
};

/**
 * 점검번호 = 연월일(YYMMDD) + 6자리 순번(000000~999999)
 * 예: 260713000000 (총 12자리)
 */
const formatEarlyScrapInspectNumber = (seqNo = 0, date = new Date()) => {
  const ymd = [
    String(date.getFullYear()).slice(-2),
    String(date.getMonth() + 1).padStart(2, '0'),
    String(date.getDate()).padStart(2, '0'),
  ].join('');
  const seq = Math.min(Math.max(Number(seqNo) || 0, 0), 999999);
  return `${ymd}${String(seq).padStart(6, '0')}`;
};

/**
 * 국토부 TS 차량정보(RECORD1) → 조기폐차 자동차 명세 필드 매핑
 */
const mapMolitVehicleToEarlyScrap = (raw = {}) => {
  const toNumberOrNull = (value) => {
    if (value === null || value === undefined || value === '') return null;
    const num = Number(String(value).replace(/[^0-9.]/g, ''));
    return Number.isFinite(num) ? num : null;
  };

  const plate =
    raw.VHRNO || raw.vhrno || raw.licensePlateNumber || '';
  const vin = raw.VIN || raw.vin || '';
  const modelYear = toNumberOrNull(raw.PRYE || raw.prye);
  const displacement = toNumberOrNull(raw.displacement);
  const seatingCapacity = toNumberOrNull(raw.maxPerson);
  const vehicleName = raw.CNM || raw.cnm || '';

  return {
    licensePlateNumber: plate,
    vin,
    vehicleName,
    modelYear,
    displacement,
    seatingCapacity,
  };
};

/**
 * 필드 블루프린트
 * - htmlFieldName: doc/조기폐차 입력창.html 의 name/id 매핑
 * - pdfFieldName: doc/조기폐차서식.pdf 항목명
 */
const EARLY_SCRAP_OWNER_BLUEPRINT = {
  name: {
    title: '성명',
    htmlFieldName: 'owner_name',
    pdfFieldName: '성명',
  },
  birthDate: {
    title: '생년월일',
    htmlFieldName: 'owner_birth',
    pdfFieldName: '생년월일',
    placeholder: '6자리',
  },
  address: {
    title: '주소',
    htmlFieldName: 'owner_address',
    pdfFieldName: '주소',
  },
  phone: {
    title: '휴대전화(차주 본인)',
    htmlFieldName: 'owner_phone',
    pdfFieldName: '휴대전화(차주 본인)',
    placeholder: '010-0000-0000',
  },
  fax: {
    title: 'FAX',
    htmlFieldName: 'owner_fax',
    pdfFieldName: 'FAX',
  },
};

const EARLY_SCRAP_VEHICLE_BLUEPRINT = {
  licensePlateNumber: {
    title: '차량번호',
    htmlFieldName: 'car_number',
    pdfFieldName: '차량번호',
  },
  vin: {
    title: '차대번호',
    htmlFieldName: 'car_serial',
    pdfFieldName: '차대번호',
  },
  vehicleClass: {
    title: '구분',
    htmlFieldName: 'car_class',
    pdfFieldName: '구분',
    defaultValue: EarlyScrapVehicleClass.AUTOMOBILE,
    valueItems: [
      { label: '자동차', value: EarlyScrapVehicleClass.AUTOMOBILE },
      {
        label: '건설기계',
        value: EarlyScrapVehicleClass.CONSTRUCTION_MACHINERY,
      },
    ],
  },
  modelYear: {
    title: '연식',
    htmlFieldName: 'car_year',
    pdfFieldName: '연식',
    unit: '년',
  },
  displacement: {
    title: '배기량',
    htmlFieldName: 'car_cc',
    pdfFieldName: '배기량',
    unit: 'CC',
  },
  vehicleName: {
    title: '차량명(건설기계명)',
    htmlFieldName: 'car_name',
    pdfFieldName: '차량명(건설기계명)',
  },
  seatingCapacity: {
    title: '정원(승합)',
    htmlFieldName: 'car_capacity',
    pdfFieldName: '정원(승합)',
    unit: '명',
  },
  heavyEquipmentSpec: {
    title: '규격(건설기계)',
    htmlFieldName: 'heavy_size',
    pdfFieldName: '규격(건설기계)',
    requiredWhen: EarlyScrapVehicleClass.CONSTRUCTION_MACHINERY,
    placeholder: '건설기계 선택시 작성',
  },
  loadWeight: {
    title: '적재중량',
    htmlFieldName: 'weight_load',
    pdfFieldName: '적재중량',
    unit: '톤',
  },
  totalWeight: {
    title: '총중량',
    htmlFieldName: 'weight_total',
    pdfFieldName: '총중량',
    unit: '톤',
  },
};

const EARLY_SCRAP_INSPECTION_BLUEPRINT = {
  identity: {
    title: '동일성 확인',
    description: '부착된 등록번호와 등록증상 일치',
    htmlFieldName: 'res_identity',
    defaultValue: EarlyScrapIdentityResult.MATCH,
    valueItems: [
      { label: '일치', value: EarlyScrapIdentityResult.MATCH },
      { label: '불일치', value: EarlyScrapIdentityResult.MISMATCH },
      { label: '식별불가', value: EarlyScrapIdentityResult.UNIDENTIFIABLE },
    ],
  },
  engine: {
    title: '원동기',
    description: '원동기(시동)작동 여부',
    htmlFieldName: 'res_engine',
    defaultValue: EarlyScrapOperationResult.OK,
    valueItems: [
      { label: '가능', value: EarlyScrapOperationResult.OK },
      { label: '불가', value: EarlyScrapOperationResult.NG },
    ],
  },
  powerTransmission: {
    title: '동력전달장치(변속기포함)',
    description:
      '변속기의 작동 여부, 변속 시 동력 전달 여부(전·후진)',
    htmlFieldName: 'res_power',
    defaultValue: EarlyScrapPowerTransmissionResult.OK,
    valueItems: [
      { label: '가능', value: EarlyScrapPowerTransmissionResult.OK },
      { label: '불가', value: EarlyScrapPowerTransmissionResult.NG },
      {
        label: '전진 또는 후진 불가',
        value: EarlyScrapPowerTransmissionResult.FORWARD_REVERSE_NG,
      },
    ],
  },
  steering: {
    title: '조향',
    description: '정상 작동 여부',
    htmlFieldName: 'res_steering',
    defaultValue: EarlyScrapOperationResult.OK,
    valueItems: [
      { label: '가능', value: EarlyScrapOperationResult.OK },
      { label: '불가', value: EarlyScrapOperationResult.NG },
    ],
  },
  brake: {
    title: '제동',
    description: '제동 가능 여부',
    htmlFieldName: 'res_brake',
    defaultValue: EarlyScrapOperationResult.OK,
    valueItems: [
      { label: '가능', value: EarlyScrapOperationResult.OK },
      { label: '불량', value: EarlyScrapOperationResult.NG },
    ],
  },
  hydraulic: {
    title: '유압장치(건설기계)',
    description: '유압유의 상태, 유압유 누출 여부',
    htmlFieldName: 'res_hydraulic',
    requiredWhen: EarlyScrapVehicleClass.CONSTRUCTION_MACHINERY,
    valueItems: [
      { label: '양호', value: 'GOOD' },
      { label: '불량', value: 'BAD' },
      { label: '단순 불량(유압유부족)', value: 'SIMPLE_BAD_OIL' },
    ],
  },
  workingPartOperation: {
    title: '유압장치(건설기계)',
    description: '각 작동부의 작동상태',
    htmlFieldName: 'res_hydraulic_operation',
    requiredWhen: EarlyScrapVehicleClass.CONSTRUCTION_MACHINERY,
    valueItems: [
      { label: '양호', value: 'GOOD' },
      { label: '불량', value: 'BAD' },
      { label: '단순 불량', value: 'SIMPLE_BAD' },
    ],
  },
  workingPartNoise: {
    title: '유압장치(건설기계)',
    description: '각 작동부의 이상소음 여부',
    htmlFieldName: 'res_hydraulic_noise',
    requiredWhen: EarlyScrapVehicleClass.CONSTRUCTION_MACHINERY,
    valueItems: [
      { label: '양호', value: 'GOOD' },
      { label: '불량', value: 'BAD' },
      { label: '단순 불량(소음)', value: 'SIMPLE_BAD_NOISE' },
    ],
  },
  exterior: {
    title: '그외 주요 부품',
    description: '차체 외관 상태',
    htmlFieldName: 'res_exterior',
    defaultValue: EarlyScrapExteriorResult.GOOD,
    valueItems: [
      { label: '양호', value: EarlyScrapExteriorResult.GOOD },
      { label: '불량', value: EarlyScrapExteriorResult.BAD },
      {
        label: '일부 파손(찌그러짐)',
        value: EarlyScrapExteriorResult.PARTIAL_DAMAGE,
      },
    ],
  },
  bumper: {
    title: '그외 주요 부품',
    description: '완충장치 상태(차량)',
    htmlFieldName: 'res_bumper',
    defaultValue: EarlyScrapConditionResult.GOOD,
    valueItems: [
      { label: '양호', value: EarlyScrapConditionResult.GOOD },
      { label: '불량', value: EarlyScrapConditionResult.BAD },
    ],
  },
  glass: {
    title: '그외 주요 부품',
    description: '유리창 파손 상태',
    htmlFieldName: 'res_glass',
    defaultValue: EarlyScrapGlassResult.GOOD,
    valueItems: [
      { label: '양호', value: EarlyScrapGlassResult.GOOD },
      { label: '불량', value: EarlyScrapGlassResult.BAD },
      {
        label: '단순 파손(일부파손)',
        value: EarlyScrapGlassResult.SIMPLE_DAMAGE,
      },
    ],
  },
  tire: {
    title: '그외 주요 부품',
    description: '타이어 장착 여부 및 상태',
    htmlFieldName: 'res_tire',
    defaultValue: EarlyScrapTireResult.GOOD,
    valueItems: [
      { label: '양호', value: EarlyScrapTireResult.GOOD },
      { label: '불량', value: EarlyScrapTireResult.BAD },
      {
        label: '단순 불량(공기압부족)',
        value: EarlyScrapTireResult.SIMPLE_BAD_AIR,
      },
    ],
  },
  lighting: {
    title: '그외 주요 부품',
    description: '등화장치 설치 상태',
    htmlFieldName: 'res_lighting',
    defaultValue: EarlyScrapLightingResult.INSTALLED,
    valueItems: [
      { label: '설치', value: EarlyScrapLightingResult.INSTALLED },
      { label: '미설치', value: EarlyScrapLightingResult.NOT_INSTALLED },
      {
        label: '단순 파손(일부 깨짐)',
        value: EarlyScrapLightingResult.SIMPLE_DAMAGE,
      },
    ],
  },
};

const EARLY_SCRAP_JUDGMENT_BLUEPRINT = {
  operationalStatus: {
    title: '정상 가동 여부',
    htmlFieldName: 'final_status',
    defaultValue: EarlyScrapOperationalJudgment.PASS,
    valueItems: [
      { label: '가 (합격/정상가동)', value: EarlyScrapOperationalJudgment.PASS },
      { label: '부 (불합격/가동불능)', value: EarlyScrapOperationalJudgment.FAIL },
    ],
  },
  failReason: {
    title: '불가 사유',
    htmlFieldName: 'fail_reason',
    placeholder: "정상가동 불능('부') 판정 시 구체적 사유 기재",
  },
  overallOpinion: {
    title: '종합 의견',
    htmlFieldName: 'total_opinion',
    placeholder: '종합 검토 의견 입력',
  },
};

const EARLY_SCRAP_ATTACHMENT_BLUEPRINT = {
  confirmationMethod: {
    title: '확인 방식',
    pdfFieldName: '첨부물',
    defaultValue: EarlyScrapConfirmationMethod.ONSITE,
    valueItems: [
      {
        label: '온라인',
        value: EarlyScrapConfirmationMethod.ONLINE,
        description:
          '온라인 대상차량확인 시스템(escar.or.kr)이용자: 시스템에 등록된 동영상으로 갈음',
      },
      {
        label: '현장',
        value: EarlyScrapConfirmationMethod.ONSITE,
        description: '현장 확인 : 사진 4매(전ㆍ후ㆍ좌ㆍ우면)',
      },
    ],
  },
};

const EARLY_SCRAP_PHOTO_BLUEPRINT = {
  positions: [
    {
      position: EarlyScrapPhotoPosition.FRONT,
      title: '전면 사진',
      htmlLabel: '전면 사진',
    },
    {
      position: EarlyScrapPhotoPosition.REAR,
      title: '후면 사진',
      htmlLabel: '후면 사진',
    },
    {
      position: EarlyScrapPhotoPosition.LEFT,
      title: '좌면 사진',
      htmlLabel: '좌면 사진',
    },
    {
      position: EarlyScrapPhotoPosition.RIGHT,
      title: '우면 사진',
      htmlLabel: '우면 사진',
    },
  ],
  requiredCount: 4,
};

const EARLY_SCRAP_INSPECTOR_BLUEPRINT = {
  shopName: {
    title: '점검장',
    pdfFieldName: '점검장',
  },
  checkDate: {
    title: '확인일',
    pdfFieldName: '년월일',
  },
  inspectorName: {
    title: '점검자',
    pdfFieldName: '(인)',
  },
};

const createEarlyScrapDefaultOwner = () => ({
  name: '',
  birthDate: '',
  address: '',
  phone: '',
  fax: '',
});

const createEarlyScrapDefaultVehicle = () => ({
  licensePlateNumber: '',
  vin: '',
  vehicleClass: EarlyScrapVehicleClass.AUTOMOBILE,
  modelYear: null,
  displacement: null,
  vehicleName: '',
  seatingCapacity: null,
  heavyEquipmentSpec: '',
  loadWeight: '',
  totalWeight: '',
});

const createEarlyScrapDefaultInspectionResults = () => ({
  identity: EarlyScrapIdentityResult.MATCH,
  engine: EarlyScrapOperationResult.OK,
  powerTransmission: EarlyScrapPowerTransmissionResult.OK,
  steering: EarlyScrapOperationResult.OK,
  brake: EarlyScrapOperationResult.OK,
  hydraulic: null,
  workingPartNoise: null,
  workingPartOperation: null,
  exterior: EarlyScrapExteriorResult.GOOD,
  bumper: EarlyScrapConditionResult.GOOD,
  glass: EarlyScrapGlassResult.GOOD,
  tire: EarlyScrapTireResult.GOOD,
  lighting: EarlyScrapLightingResult.INSTALLED,
});

const createEarlyScrapDefaultJudgment = () => ({
  operationalStatus: EarlyScrapOperationalJudgment.PASS,
  failReason: '',
  overallOpinion: '',
});

const createEarlyScrapDefaultPhotos = () => ({
  photoGroupId: '',
  items: {
    [EarlyScrapPhotoPosition.FRONT]: null,
    [EarlyScrapPhotoPosition.REAR]: null,
    [EarlyScrapPhotoPosition.LEFT]: null,
    [EarlyScrapPhotoPosition.RIGHT]: null,
  },
});

const createEarlyScrapDefaultInspector = () => ({
  shopName: '',
  checkDate: '',
  inspectorName: '',
});

/** 점검료 (성능점검 paymentInformation 중 점검료 관련 필드) */
const createEarlyScrapDefaultPaymentInformation = () => ({
  checkPriceTableId: 0,
  checkPriceItemId: '',
  checkPrice: 0,
  checkPriceVAT: 0,
  checkPricePaidBy: 'TRAN',
  discountedCheckPrice: 0,
  discountReason: '',
  checkPricePaidYN: YN.NO,
  checkPricePaidAt: null,
});

/** 조기폐차 확인서 전체 폼 기본값 */
const createEarlyScrapDefaultForm = () => ({
  inspectNumber: '',
  confirmationMethod: EarlyScrapConfirmationMethod.ONSITE,
  owner: createEarlyScrapDefaultOwner(),
  paymentInformation: createEarlyScrapDefaultPaymentInformation(),
  vehicle: createEarlyScrapDefaultVehicle(),
  inspectionResults: createEarlyScrapDefaultInspectionResults(),
  judgment: createEarlyScrapDefaultJudgment(),
  photos: createEarlyScrapDefaultPhotos(),
  inspector: createEarlyScrapDefaultInspector(),
});

/** HTML 입력창 name 속성 → 내부 데이터 경로 매핑 */
const EARLY_SCRAP_HTML_FIELD_MAP = {
  inspect_num: 'inspectNumber',
  owner_name: 'owner.name',
  owner_birth: 'owner.birthDate',
  owner_address: 'owner.address',
  owner_phone: 'owner.phone',
  owner_fax: 'owner.fax',
  car_number: 'vehicle.licensePlateNumber',
  car_serial: 'vehicle.vin',
  car_class: 'vehicle.vehicleClass',
  car_year: 'vehicle.modelYear',
  car_cc: 'vehicle.displacement',
  car_name: 'vehicle.vehicleName',
  car_capacity: 'vehicle.seatingCapacity',
  heavy_size: 'vehicle.heavyEquipmentSpec',
  weight_load: 'vehicle.loadWeight',
  weight_total: 'vehicle.totalWeight',
  res_identity: 'inspectionResults.identity',
  res_engine: 'inspectionResults.engine',
  res_power: 'inspectionResults.powerTransmission',
  res_steering: 'inspectionResults.steering',
  res_brake: 'inspectionResults.brake',
  res_hydraulic: 'inspectionResults.hydraulic',
  res_hydraulic_operation: 'inspectionResults.workingPartOperation',
  res_hydraulic_noise: 'inspectionResults.workingPartNoise',
  res_exterior: 'inspectionResults.exterior',
  res_bumper: 'inspectionResults.bumper',
  res_glass: 'inspectionResults.glass',
  res_tire: 'inspectionResults.tire',
  res_lighting: 'inspectionResults.lighting',
  final_status: 'judgment.operationalStatus',
  fail_reason: 'judgment.failReason',
  total_opinion: 'judgment.overallOpinion',
};

/** HTML 라디오 value(한글) → 내부 enum code 변환 */
const EARLY_SCRAP_HTML_VALUE_TO_ENUM = {
  car_class: {
    자동차: EarlyScrapVehicleClass.AUTOMOBILE,
    건설기계: EarlyScrapVehicleClass.CONSTRUCTION_MACHINERY,
  },
  res_identity: {
    일치: EarlyScrapIdentityResult.MATCH,
    불일치: EarlyScrapIdentityResult.MISMATCH,
    식별불가: EarlyScrapIdentityResult.UNIDENTIFIABLE,
  },
  res_engine: {
    가능: EarlyScrapOperationResult.OK,
    불가: EarlyScrapOperationResult.NG,
  },
  res_power: {
    가능: EarlyScrapPowerTransmissionResult.OK,
    불가: EarlyScrapPowerTransmissionResult.NG,
    전후진불가: EarlyScrapPowerTransmissionResult.FORWARD_REVERSE_NG,
  },
  res_steering: {
    가능: EarlyScrapOperationResult.OK,
    불가: EarlyScrapOperationResult.NG,
  },
  res_brake: {
    가능: EarlyScrapOperationResult.OK,
    불량: EarlyScrapOperationResult.NG,
  },
  res_hydraulic: {
    양호: 'GOOD',
    불량: 'BAD',
    '단순 불량(유압유부족)': 'SIMPLE_BAD_OIL',
  },
  res_hydraulic_operation: {
    양호: 'GOOD',
    불량: 'BAD',
    '단순 불량': 'SIMPLE_BAD',
  },
  res_hydraulic_noise: {
    양호: 'GOOD',
    불량: 'BAD',
    '단순 불량(소음)': 'SIMPLE_BAD_NOISE',
  },
  res_exterior: {
    양호: EarlyScrapExteriorResult.GOOD,
    불량: EarlyScrapExteriorResult.BAD,
    '일부 파손(찌그러짐)': EarlyScrapExteriorResult.PARTIAL_DAMAGE,
  },
  res_bumper: {
    양호: EarlyScrapConditionResult.GOOD,
    불량: EarlyScrapConditionResult.BAD,
  },
  res_glass: {
    양호: EarlyScrapGlassResult.GOOD,
    불량: EarlyScrapGlassResult.BAD,
    '단순 파손(일부파손)': EarlyScrapGlassResult.SIMPLE_DAMAGE,
  },
  res_tire: {
    양호: EarlyScrapTireResult.GOOD,
    불량: EarlyScrapTireResult.BAD,
    '단순 불량(공기압부족)': EarlyScrapTireResult.SIMPLE_BAD_AIR,
  },
  res_lighting: {
    설치: EarlyScrapLightingResult.INSTALLED,
    미설치: EarlyScrapLightingResult.NOT_INSTALLED,
    '단순 파손(일부 깨짐)': EarlyScrapLightingResult.SIMPLE_DAMAGE,
  },
  final_status: {
    가: EarlyScrapOperationalJudgment.PASS,
    부: EarlyScrapOperationalJudgment.FAIL,
  },
};

const isEarlyScrapConstructionMachinery = (vehicleClass) =>
  vehicleClass === EarlyScrapVehicleClass.CONSTRUCTION_MACHINERY;

export {
  EARLY_SCRAP_FORM_META,
  EARLY_SCRAP_OWNER_BLUEPRINT,
  EARLY_SCRAP_VEHICLE_BLUEPRINT,
  EARLY_SCRAP_INSPECTION_BLUEPRINT,
  EARLY_SCRAP_JUDGMENT_BLUEPRINT,
  EARLY_SCRAP_ATTACHMENT_BLUEPRINT,
  EARLY_SCRAP_PHOTO_BLUEPRINT,
  EARLY_SCRAP_INSPECTOR_BLUEPRINT,
  EARLY_SCRAP_HTML_FIELD_MAP,
  EARLY_SCRAP_HTML_VALUE_TO_ENUM,
  formatEarlyScrapInspectNumber,
  mapMolitVehicleToEarlyScrap,
  createEarlyScrapDefaultOwner,
  createEarlyScrapDefaultVehicle,
  createEarlyScrapDefaultInspectionResults,
  createEarlyScrapDefaultJudgment,
  createEarlyScrapDefaultPhotos,
  createEarlyScrapDefaultInspector,
  createEarlyScrapDefaultPaymentInformation,
  createEarlyScrapDefaultForm,
  isEarlyScrapConstructionMachinery,
};
